'use strict';

import React, { Component } from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import '@ag-grid-community/core/dist/styles/ag-grid.css';
import "@ag-grid-enterprise/all-modules/dist/styles/ag-theme-balham.css";
import { LoadingPortContext, resolvePageAccess } from '../../context/LoadingPortContext';
import { getHyphenDDMMMYYYYHHMM } from '../common/utils';
var redirectURL = require('../redirectURL');

// Shared by the Trip Results column's valueGetter (truncated by the cell) and
// tooltipValueGetter (full text on hover) so the two can never drift apart.
function tripResultsValue(params) {
    return (params.data.trip_results || []).map(function (t) {
        return "Trip " + t.trip_no + ": " + (t.status === 'applied' ? "Applied (" + t.row_count + ")" : "Rejected");
    }).join(', ');
}

// Truck Load Automation upload audit log - one row per transaction with how many
// times its plan data has been uploaded; the Actions "View" button toggles an
// inline master/detail sub-grid of that transaction's individual upload events,
// and "View" on an event shows exactly what was submitted in that one attempt.
// Page access + loading port resolution follows the same resolvePageAccess
// pattern as lmtransporters.js/lmvehiclecapacity.js (admin branch resolves lpNames
// directly; non-admin branch checks lm_page_access via checkUserCurrentPageAccess) -
// so this screen needs an lm_page_access row per non-admin user/role before they
// can open it, same as those two screens.
export default class LmTruckloadAuditLog extends Component {
    static contextType = LoadingPortContext;

    constructor(props, context) {
        super(props, context);
        this._lastLoadingPortCode = context ? context.loading_port_code : null;
        this.state = {
            modules: AllModules,
            rowData: [],
            allHistory: [],
            pageAccess: 0,
            loading_port_code: '',
            comp_code: '',
            lpNames: [],
            detailSlide: '',
            detailRows: [],
            loadshow: 'show-n',
            overly: 'show-n'
        };
    }

    componentDidMount() {
        this.pageAccess();
    }

    async pageAccess() {
        this.setState({ loadshow: 'show-m', overly: 'show-m' });
        var currentUrl = this.props.match.path;
        var resolved = await resolvePageAccess(currentUrl, this.context);
        if (!resolved) {
            // No access (already alerted+redirected), empty port list, or a logged request error.
            this.setState({ loadshow: 'show-n', overly: 'show-n' });
            return;
        }
        this._lastLoadingPortCode = resolved.loading_port_code;
        this.setState({
            pageAccess: resolved.pageAccess,
            loading_port_code: resolved.loading_port_code,
            comp_code: resolved.comp_code,
            lpNames: resolved.lpNames,
        });
        await this.loadHistory(resolved.loading_port_code);
        this.setState({ loadshow: 'show-n', overly: 'show-n' });
    }

    // Same reactivity lmtransporters.js/lmvehiclecapacity.js have - the navbar's
    // own Loading Port select (Header, mounted once for the whole desktop app)
    // drives LoadingPortContext, and this screen re-resolves + refetches when it
    // changes to a port that's actually valid for this screen's own lpNames.
    componentDidUpdate() {
        if (this.context && this.context.loading_port_code !== this._lastLoadingPortCode) {
            this._lastLoadingPortCode = this.context.loading_port_code;
            this.onGlobalPortChange();
        }
    }

    onGlobalPortChange = () => {
        var selectedPlant = this.context.loading_port_code;
        if (!selectedPlant || !this.state.lpNames.some(each => each.loading_port_code == selectedPlant)) {
            // Not a valid port for this screen - keep this screen's own selection, don't refetch.
            return;
        }
        this.setState({ loadshow: 'show-m', overly: 'show-m' });
        redirectURL.post("/lmconsignees/getLoadingPlantDetails", { selectedPlant: selectedPlant }).then((response) => {
            var res = response.data;
            this.setState({
                loading_port_code: selectedPlant,
                comp_code: res[0].comp_code,
            }, async () => {
                await this.loadHistory();
                this.setState({ loadshow: 'show-n', overly: 'show-n' });
            });
        }).catch((e) => {
            console.log(e);
            this.setState({ loadshow: 'show-n', overly: 'show-n' });
        });
    };

    // The backend stores/returns one flat row per uploaded HU (sharing an
    // upload_batch_id per upload event) - grouping into "events" (by
    // upload_batch_id) and "per-transaction counts" (by transaction_id) all happens
    // here, client-side, from this one fetch. No separate detail round-trip needed -
    // viewUploadDetail below just filters the already-fetched array.
    // loadingPortCode param (not just this.state.loading_port_code) since the very
    // first call happens right after the componentDidMount setState that sets
    // it - setState isn't synchronous, so reading state back immediately after
    // risks the pre-update value. loading_port_code is required by the backend
    // now, so this can no longer omit it either way.
    loadHistory = (loadingPortCode) => {
        var params = { loading_port_code: loadingPortCode || this.state.loading_port_code };
        console.log('[truckload] getUploadHistoryList', params);
        return redirectURL.post("/lmconsignees/truckload/getUploadHistoryList", params).then((response) => {
            var allHistory = response.data || [];
            var byTransaction = {};
            var seenBatchPerTransaction = {};
            allHistory.forEach(function (h) {
                if (!byTransaction[h.transaction_id]) {
                    byTransaction[h.transaction_id] = {
                        transaction_id: h.transaction_id,
                        loading_port_code: h.loading_port_code,
                        upload_count: 0,
                        last_uploaded_at: h.uploaded_at
                    };
                    seenBatchPerTransaction[h.transaction_id] = new Set();
                }
                if (!seenBatchPerTransaction[h.transaction_id].has(h.upload_batch_id)) {
                    seenBatchPerTransaction[h.transaction_id].add(h.upload_batch_id);
                    byTransaction[h.transaction_id].upload_count += 1;
                }
                if (new Date(h.uploaded_at) > new Date(byTransaction[h.transaction_id].last_uploaded_at)) {
                    byTransaction[h.transaction_id].last_uploaded_at = h.uploaded_at;
                }
            });
            this.setState({ allHistory: allHistory, rowData: Object.values(byTransaction) });
        }).catch((e) => {
            console.log(e);
        });
    };

    // Grouped by upload_batch_id, same shape the "View Uploads" slider used to
    // fetch on click - now read directly by the main grid's master/detail
    // sub-grid (getDetailRowData below) instead of a separate slide-out panel.
    getUploadsForTransaction = (transactionId) => {
        var rows = this.state.allHistory.filter(function (h) { return h.transaction_id === transactionId; });
        var byBatch = {};
        rows.forEach(function (r) {
            if (!byBatch[r.upload_batch_id]) {
                byBatch[r.upload_batch_id] = {
                    upload_batch_id: r.upload_batch_id,
                    filename: r.filename,
                    uploaded_at: r.uploaded_at,
                    uploaded_by: r.uploaded_by,
                    trip_results: r.trip_results,
                    row_count: 0
                };
            }
            byBatch[r.upload_batch_id].row_count += 1;
        });
        return Object.values(byBatch);
    };

    viewUploadDetail = (uploadBatchId) => {
        var rows = this.state.allHistory.filter(function (h) { return h.upload_batch_id === uploadBatchId; });
        this.setState({
            detailRows: rows,
            detailSlide: 'slider-translate-75p',
            overly: 'show-m'
        });
    };

    hideAll = () => {
        this.setState({ detailSlide: '', overly: 'show-n' });
    };

    render() {
        var columnDefs = [
            {
                headerName: "Actions", flex: 0, field: "actions", width: 130, filter: false, sortable: false, resizable: true,
                cellRenderer: (params) => {
                    var btn = document.createElement('button');
                    btn.innerHTML = "View";
                    btn.className = "pill-btn pill-btn-info";
                    btn.style.padding = "1px 19px";
                    btn.style.fontSize = "12px"; btn.style.borderRadius = "5px";
                    btn.addEventListener('click', () => params.node.setExpanded(!params.node.expanded));
                    return btn;
                }
            },
            { headerName: "Transaction ID", field: "transaction_id", width: 260, filter: true, resizable: true },
            { headerName: "Upload Count", field: "upload_count", width: 140, filter: true, resizable: true },
            {
                headerName: "Last Uploaded",
                field: "last_uploaded_at",
                width: 180,
                filter: true,
                resizable: true,
                valueGetter: function (params) {
                    return params.data.last_uploaded_at ? getHyphenDDMMMYYYYHHMM(params.data.last_uploaded_at) : "";
                }
            }
        ];

        var eventColumnDefs = [
            {
                headerName: "Actions", field: "view", width: 130, flex: 0, filter: false,
                cellRenderer: (params) => {
                    var btn = document.createElement('button');
                    btn.innerHTML = "View";
                    btn.className = "pill-btn pill-btn-info";
                    btn.style.padding = "1px 19px";
                    btn.style.fontSize = "12px"; btn.style.borderRadius = "5px";
                    btn.addEventListener('click', () => this.viewUploadDetail(params.data.upload_batch_id));
                    return btn;
                }
            },
            { headerName: "Filename", field: "filename", width: 220, filter: true, resizable: true },
            {
                headerName: "Uploaded At", field: "uploaded_at", width: 170, filter: true, resizable: true,
                valueGetter: function (params) { return params.data.uploaded_at ? getHyphenDDMMMYYYYHHMM(params.data.uploaded_at) : ""; }
            },
            {
                headerName: "Uploaded By", field: "uploaded_by", width: 160, filter: true, resizable: true,
                valueGetter: function (params) { return params.data.uploaded_by ? (params.data.uploaded_by.username || params.data.uploaded_by.staff_id || "") : ""; }
            },
            {
                headerName: "Trip Results", field: "trip_results", width: 280, filter: false, resizable: true,
                valueGetter: tripResultsValue,
                tooltipValueGetter: tripResultsValue
            }
        ];

        var detailColumnDefs = [
            { headerName: "Trip No", field: "trip_no", width: 100, filter: true, resizable: true },
            { headerName: "Consignee", field: "consignee", width: 140, filter: true, resizable: true },
            { headerName: "Doc No", field: "doc_no", width: 140, filter: true, resizable: true },
            { headerName: "HU", field: "hu", width: 160, filter: true, resizable: true },
            { headerName: "Doc Type", field: "doc_type", width: 120, filter: true, resizable: true },
            { headerName: "Trip Type", field: "trip_type", width: 120, filter: true, resizable: true },
            { headerName: "Plan Date", field: "plan_date", width: 130, filter: true, resizable: true }
        ];

        // Same footer/sidebar setup as lmvehiclecapacity.js's grid - row-count
        // status bar plus the Columns/Filters tool panel.
        var mainStatusBar = {
            statusPanels: [
                { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
                { statusPanel: "agFilteredRowCountComponent" }
            ]
        };
        var mainSideBar = {
            toolPanels: [
                { id: "columns", labelDefault: "Columns", labelKey: "columns", iconKey: "columns", toolPanel: "agColumnsToolPanel" },
                { id: "filters", labelDefault: "Filters", labelKey: "filters", iconKey: "filter", toolPanel: "agFiltersToolPanel" }
            ]
        };

        // The "View" button in Actions toggles this inline sub-grid of the
        // transaction's uploads instead of a slider - "View" inside that sub-grid
        // still opens the slide-out with the actual uploaded rows.
        var gridOptions = {
            masterDetail: true,
            detailRowHeight: 260,
            detailCellRendererParams: {
                detailGridOptions: {
                    columnDefs: eventColumnDefs,
                    defaultColDef: { flex: 1, sortable: true, resizable: true },
                    enableBrowserTooltips: true,
                    rowHeight: 42,
                    headerHeight: 38,
                    statusBar: {
                        statusPanels: [
                            { statusPanel: "agTotalAndFilteredRowCountComponent", align: "right" }
                        ]
                    }
                },
                getDetailRowData: (params) => {
                    params.successCallback(this.getUploadsForTransaction(params.data.transaction_id));
                }
            }
        };

        return (
            <div className="container-fluid" style={{ height: "100%" }}>
                <div className={"dataLoadpage " + this.state.loadshow}></div>
                <div className={"dataLoadpageimg " + this.state.loadshow}>
                    <div className="loader-box"><div className="loader-box"><div className="rotate dashed colored"></div></div></div>
                </div>
                <div className={"overlay-part " + this.state.overly} onClick={this.hideAll}></div>
                <div className="card col-xl-12" style={{ borderRadius: "10px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
                    <div className="d-flex align-items-center" style={{ gap: "14px", padding: "14px 18px" }}>
                        <div style={{ width: "5px", height: "26px", background: "linear-gradient(180deg,#1976d2,#42a5f5)", borderRadius: "3px", flexShrink: 0 }}></div>
                        <h5 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#1a237e" }}>Loading Plan Audit Log</h5>
                    </div>
                    <div className="col-xl-12 col-lg-12" style={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
                        <div className="ag-theme-balham grid-wrapper mt-3 mb-3" style={{ flex: 1, minHeight: 0 }}>
                            <AgGridReact
                                modules={this.state.modules}
                                columnDefs={columnDefs}
                                rowData={this.state.rowData}
                                defaultColDef={{ flex: 1, sortable: true, filter: true, floatingFilter: true, resizable: true }}
                                gridOptions={gridOptions}
                                statusBar={mainStatusBar}
                                sideBar={mainSideBar}
                                animateRows
                                rowHeight={45}
                                headerHeight={45}
                            />
                        </div>
                    </div>
                </div>

                <div className={"slide-r " + this.state.detailSlide} style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
                    <h3 className="subH" style={{ background: "linear-gradient(135deg,#1976d2,#1a237e)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", flexShrink: 0 }}>
                        <span>Uploaded Data</span>
                        <span onClick={this.hideAll} style={{ cursor: "pointer", fontSize: "16px" }}>✕</span>
                    </h3>
                    <div className="slide-r-body" style={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: "16px 18px" }}>
                        <div className="ag-theme-balham" style={{ width: "100%", flex: 1, minHeight: 0 }}>
                            <AgGridReact
                                modules={this.state.modules}
                                columnDefs={detailColumnDefs}
                                rowData={this.state.detailRows}
                                defaultColDef={{ flex: 1, sortable: true, filter: true, resizable: true }}
                                statusBar={{
                                    statusPanels: [
                                        { statusPanel: "agTotalAndFilteredRowCountComponent", align: "right" }
                                    ]
                                }}
                                rowHeight={45}
                                headerHeight={45}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
